<template>
  <MediaLayout
    v-if="fsm.currentState === 'media'"
    :can-continue="true"
    @continue="fsm.send('CONTINUE')"
  >
    <MediaImageViewer :url="message.image ?? ''" />
    <template #button>
      {{ c('return_to_message') }}
    </template>
  </MediaLayout>
  <InterviewLayout v-else-if="fsm.currentState === 'message'">
    <template #header>
      <InterviewHeader :brand-image="brandImage" :title-markdown="title" />
    </template>

    <div class="message-prompt">
      <div class="message-prompt__background">
        <div
          :class="{
            'message-prompt__container': true,
            'message-prompt__container--vertically-centered': !message.image,
          }"
        >
          <button
            v-if="message.image"
            class="message-prompt__image"
            data-test-id="MessagePrompt_Image"
            @click="fsm.send('VIEW_MEDIA')"
          >
            <img :src="message.image" width="800" height="600" alt="" />
          </button>
          <!-- eslint-disable vue/no-v-html -->
          <h1 data-test-id="MessagePrompt_Text" class="message-prompt__text" v-html="messageHtml" />
        </div>
      </div>
    </div>

    <template #footer>
      <InterviewFooter :is-loading="false" :submit-text="c('next')" @continue="$emit('next')" />
    </template>
  </InterviewLayout>
</template>
<script lang="ts">
import { useFSM } from '@fsmify/vue'
import { computed, defineComponent, type PropType, reactive } from 'vue'

import MediaImageViewer from '../../../core/components/dumb/MediaImageViewer.vue'
import MediaLayout from '../../../core/components/dumb/MediaLayout.vue'
import InterviewFooter from '../../../interview/components/dumb/InterviewFooter.vue'
import InterviewHeader from '../../../interview/components/dumb/InterviewHeader.vue'
import InterviewLayout from '../../../interview/components/dumb/InterviewLayout.vue'
import { parseUserMarkdown } from '../../../interview/utils/parseUserMarkdown'
import { c } from '../../../intl'
import { type Message } from '../../../message/models/Message'

export default defineComponent({
  components: { InterviewFooter, InterviewLayout, MediaLayout, MediaImageViewer, InterviewHeader },
  props: {
    message: {
      type: Object as PropType<Pick<Message, 'image' | 'text'>>,
      required: true,
    },
    title: {
      type: String,
      default: undefined,
      required: false,
    },
    brandImage: { type: [String, null], required: false, default: undefined },
  },
  emits: {
    next: () => true,
  },
  setup(props) {
    const messageHtml = computed(() =>
      parseUserMarkdown(props.message.text, { enabled: ['linkify'] }),
    )
    const fsm = useFSM({
      initialState: 'message',
      states: {
        message: { VIEW_MEDIA: 'media' },
        media: { CONTINUE: 'message' },
      },
    })
    return { fsm: reactive(fsm), c, messageHtml }
  },
})
</script>
<style lang="postcss">
.message-prompt {
  background-color: #f6f9fd;
  padding: 1rem;
  height: 100%;
  overflow: hidden;
  position: relative;

  &__background {
    background-color: #fff;
    height: 100%;
  }

  &__container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-blue-grey);
    overflow-y: scroll;
    padding: 2rem;
    background:
      linear-gradient(white 30%, rgba(255, 255, 255, 0)) center top,
      linear-gradient(rgba(255, 255, 255, 0), white 70%) center bottom,
      radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0)) center top,
      radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0)) center
        bottom;
    background-repeat: no-repeat;
    background-size:
      100% 40px,
      100% 40px,
      100% 20px,
      100% 20px;
    background-attachment: local, local, scroll, scroll;
  }

  &__image {
    background: none;
    width: 100%;
    position: relative;
    border: none;
    cursor: pointer;
    margin-bottom: var(--attest-spacing-8);
    padding: 0;

    img {
      height: auto;
      max-height: 50vh;
      max-width: 100%;
      object-fit: cover;
    }

    &::after {
      content: '';
      position: absolute;
      right: -10px;
      bottom: -10px;
      width: 25px;
      height: 25px;
      border-radius: 50%;
      background-color: var(--color-selected);
      background-image: url('../../../interview/assets/expand-white.svg');
      background-size: auto 11px;
      background-repeat: no-repeat;
      background-position: center;
    }
  }

  &__text {
    color: #036;
    font-size: var(--attest-font-size-m);
    text-align: center;
    white-space: break-spaces;

    a {
      color: #4aacff;
      cursor: pointer;
      text-decoration: none;
    }
  }
}
</style>
